import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, Navigation, X } from 'lucide-react';

const LOCATION_PROMPTED_KEY = 'location_permission_prompted';

export const LocationPermissionModal = ({ isOpen, onClose, onLocationGranted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;
      
      // Store location in localStorage
      try {
        localStorage.setItem('user_location', JSON.stringify({ latitude, longitude }));
        localStorage.setItem(LOCATION_PROMPTED_KEY, 'true');
      } catch (e) {
        console.warn('Could not save location to localStorage');
      }

      if (onLocationGranted) {
        onLocationGranted({ latitude, longitude });
      }
      onClose();
    } catch (err) {
      console.error('Location error:', err);
      if (err.code === 1) {
        setError('Location permission denied. You can enable it later in your browser settings.');
      } else if (err.code === 2) {
        setError('Could not determine your location. Please try again.');
      } else {
        setError('Location request timed out. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    try {
      localStorage.setItem(LOCATION_PROMPTED_KEY, 'true');
    } catch (e) {
      console.warn('Could not save to localStorage');
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center">Enable Location Services</DialogTitle>
          <DialogDescription className="text-center">
            Allow Next Gig to access your location to discover events happening near you. 
            We'll show you the best experiences in your area.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-3">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <Navigation className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">Find nearby events</p>
              <p className="text-xs text-muted-foreground">See events happening close to you</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
            <MapPin className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium">Distance information</p>
              <p className="text-xs text-muted-foreground">Know how far events are from your location</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button 
            onClick={requestLocation} 
            className="w-full btn-secondary"
            disabled={loading}
            data-testid="enable-location-btn"
          >
            {loading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Getting location...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 mr-2" />
                Enable Location
              </>
            )}
          </Button>
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            className="w-full"
            data-testid="skip-location-btn"
          >
            Maybe Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to check if we should show the modal
export const shouldShowLocationPrompt = () => {
  try {
    const prompted = localStorage.getItem(LOCATION_PROMPTED_KEY);
    return !prompted;
  } catch (e) {
    return false;
  }
};

// Helper to get stored location
export const getStoredLocation = () => {
  try {
    const location = localStorage.getItem('user_location');
    return location ? JSON.parse(location) : null;
  } catch (e) {
    return null;
  }
};

export default LocationPermissionModal;
